/**
 * 媒體畫廊組件
 *
 * 圖片只有兩種互斥狀態：
 *   not-selected – 預設不列印（灰紅外觀，顯示「不列印」浮水印）
 *   selected     – 右上角綠色數字圓（要列印）
 *
 * 頂部篩選欄：
 *   「不顯示已選用圖片」「不顯示未選取的圖片」
 */

import React, { useState } from 'react';
import { Card, Empty, Spin, Image, Tag, Typography, Button, Space, Checkbox } from 'antd';
import { FileImageOutlined, SelectOutlined, ClearOutlined } from '@ant-design/icons';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { normalizeStatusForDisplay } from '../utils/mediaGrouping';
import { getPvDisplayLabel } from '../utils/exportMediaShared';
import { normalizeMediaFilePath } from '../../../../services/uiPreferenceApi';
import { makeSubGroupKey } from '../utils/filterExcludedMedia';

const { Text, Title } = Typography;

const getImageUrl = (filePath) => {
  if (!filePath) return '';
  if (filePath.startsWith('http://') || filePath.startsWith('https://') || filePath.startsWith('data:')) {
    return filePath;
  }
  if (filePath.startsWith('/')) return filePath;
  return `/Uploads/${filePath}`;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const MediaGallery = ({
  groupedMedia,
  settings = {},
  onImageClick,
  groupingPvs = [],
  pvDefinitions = [],
  groupingEformFields = [],
  selectedByGroup = {},     // { subGroupKey: [normalizedPath, ...] } 有序陣列
  onSelectionChange,        // (subGroupKey, newOrderedPaths) => void
  excludedByGroup = {},     // { subGroupKey: [normalizedPath, ...] } 不列印標記
  onExcludedChange,         // (subGroupKey, newExcludedPaths) => void
  // controlled display filters (persisted by parent); if undefined → use local state
  hideSelected: hideSelectedProp,
  hideExcluded: hideExcludedProp,
  onHideSelectedChange,     // (bool) => void
  onHideExcludedChange,     // (bool) => void
}) => {
  const { t } = useLanguage();
  const [imageLoadStates, setImageLoadStates] = useState({});
  // local state fallback when parent does not control the value
  const [hideSelectedLocal, setHideSelectedLocal] = useState(true);
  const [hideExcludedLocal, setHideExcludedLocal] = useState(true);

  const isControlledSelected = hideSelectedProp !== undefined;
  const isControlledExcluded = hideExcludedProp !== undefined;
  const hideSelected = isControlledSelected ? hideSelectedProp : hideSelectedLocal;
  const hideExcluded = isControlledExcluded ? hideExcludedProp : hideExcludedLocal;

  const setHideSelected = (val) => {
    if (isControlledSelected) { if (onHideSelectedChange) onHideSelectedChange(val); }
    else setHideSelectedLocal(val);
  };
  const setHideExcluded = (val) => {
    if (isControlledExcluded) { if (onHideExcludedChange) onHideExcludedChange(val); }
    else setHideExcludedLocal(val);
  };

  const { imageGap = 5, minImageWidth = 50 } = settings;

  // 全部圖片 URL（供預覽組，不受選取過濾）
  const allImageUrls = React.useMemo(() => {
    const urls = [];
    groupedMedia.forEach(group => {
      (group.subGroups || []).forEach(subGroup => {
        subGroup.mediaFiles.forEach(file => {
          urls.push(getImageUrl(file.filePath));
        });
      });
    });
    return urls;
  }, [groupedMedia]);

  const handleImageLoad = (id) => setImageLoadStates(prev => ({ ...prev, [id]: 'loaded' }));
  const handleImageError = (id) => setImageLoadStates(prev => ({ ...prev, [id]: 'error' }));

  // 取得某圖在 subGroup 中的選取順序（1-based），未選回傳 null
  const getFileOrder = (subGroupKey, filePath) => {
    const normalized = normalizeMediaFilePath(filePath);
    const paths = selectedByGroup[subGroupKey] || [];
    const idx = paths.indexOf(normalized);
    return idx === -1 ? null : idx + 1;
  };

  // ── 點右上圓圈：selected ↔ not-selected（兩態切換）──────────────────────────
  const handleToggleSelect = (subGroupKey, filePath) => {
    if (!onSelectionChange) return;
    const normalized = normalizeMediaFilePath(filePath);
    const selPaths = [...(selectedByGroup[subGroupKey] || [])];
    const selIdx = selPaths.indexOf(normalized);
    if (selIdx !== -1) {
      // 已選 → 取消選取（回到預設不列印）
      selPaths.splice(selIdx, 1);
    } else {
      // 未選 → 選取
      selPaths.push(normalized);
    }
    onSelectionChange(subGroupKey, selPaths);
  };

  // 全選某 subGroup
  const handleSelectAll = (subGroupKey, mediaFiles) => {
    if (!onSelectionChange) return;
    const paths = mediaFiles.map(f => normalizeMediaFilePath(f.filePath)).filter(Boolean);
    onSelectionChange(subGroupKey, paths);
  };

  // 清除某 subGroup 的所有選取（回到全部不列印）
  const handleClearAll = (subGroupKey) => {
    if (!onSelectionChange) return;
    onSelectionChange(subGroupKey, []);
  };

  if (!groupedMedia || groupedMedia.length === 0) {
    return (
      <Card>
        <Empty
          description={t('reports.noMediaFiles') || '沒有找到媒體文件'}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <div className="media-gallery">
      <style>{`
        .media-gallery { width: 100%; }
        .gallery-filter-bar {
          display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
          padding: 8px 12px; margin-bottom: 16px;
          background: #fafafa; border: 1px solid #f0f0f0; border-radius: 6px;
          font-size: 13px;
        }
        .gallery-filter-bar-label { color: #888; font-size: 12px; }

        /* ── 大泡泡框：每個 group 明顯卡片 ── */
        .gallery-group {
          margin-bottom: 28px;
          background: #fff;
          border: 2px solid #d6e4ff;
          border-radius: 18px;
          padding: 20px 22px 18px;
          box-shadow: 0 6px 24px rgba(24, 98, 255, 0.07), 0 2px 8px rgba(0,0,0,0.06);
        }

        /* ── 分組 Title 區：加大字體 + 明顯背景色 ── */
        .gallery-group-title {
          margin-bottom: 18px;
          padding: 12px 18px;
          background: linear-gradient(135deg, #e8f0fe 0%, #dbeafe 100%);
          border-radius: 12px;
          border-left: 6px solid #2563eb;
        }
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(${minImageWidth * 4}px, 1fr));
          gap: ${imageGap}px;
          width: 100%;
        }
        /* ── 正方形縮圖框，overflow hidden 裁切超出部分 ── */
        .gallery-item {
          position: relative;
          border-radius: 4px;
          overflow: hidden;
          background: #f5f5f5;
          aspect-ratio: 1 / 1;
          opacity: 0.5;
          transition: transform 0.2s, box-shadow 0.2s, opacity 0.15s;
        }
        .gallery-item:hover {
          transform: scale(1.02);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          opacity: 0.8;
        }
        .gallery-item.gallery-item-selected {
          opacity: 1;
          outline: 2.5px solid #52c41a;
          outline-offset: -2px;
          box-shadow: 0 0 0 3px rgba(82,196,26,0.18);
        }
        .gallery-item.gallery-item-selected:hover { opacity: 1; }
        /* 未選取 = 預設不列印（統一樣式，不再區分 neutral / excluded） */
        .gallery-item.gallery-item-not-selected {
          opacity: 0.42;
          outline: 1.5px solid rgba(255, 77, 79, 0.5);
          outline-offset: -2px;
        }
        .gallery-item.gallery-item-not-selected:hover { opacity: 0.68; }

        /* ── 圖片填滿正方形框，置中裁切（點開才看完整） ── */
        .gallery-item-image {
          display: block;
          width: 100%;
          height: 100%;
        }
        /* antd Image wrapper 需跟容器等高 */
        .gallery-item .ant-image {
          display: block;
          width: 100%;
          height: 100%;
        }
        /* 實際 img：置中裁切填滿 */
        .gallery-item .ant-image-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
        }
        .gallery-item-overlay {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
          color: white;
          padding: 8px;
          font-size: 12px;
          opacity: 0;
          transition: opacity 0.2s;
          pointer-events: none;
        }
        .gallery-item:hover .gallery-item-overlay { opacity: 1; }
        .gallery-item-loading {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          background: #fafafa;
        }
        .gallery-item-error {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          background: #fff1f0; color: #ff4d4f; font-size: 12px;
        }
        /* 右上角：已選綠色數字徽章 */
        .gallery-sel-badge {
          position: absolute; top: 6px; right: 6px; z-index: 5;
          width: 28px; height: 28px; border-radius: 50%;
          background: #52c41a; color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 700;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.35);
          transition: transform 0.15s;
          user-select: none;
        }
        .gallery-sel-badge:hover { transform: scale(1.18); }
        /* 右上角：未選空心圈（hover 才顯示） */
        .gallery-sel-empty {
          position: absolute; top: 6px; right: 6px; z-index: 5;
          width: 28px; height: 28px; border-radius: 50%;
          background: rgba(255,255,255,0.2);
          border: 2.5px solid rgba(255,255,255,0.85);
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.2s, transform 0.15s;
        }
        .gallery-sel-empty:hover { transform: scale(1.18); }
        .gallery-item:hover .gallery-sel-empty { opacity: 1; }
        /* 不列印浮水印 */
        .gallery-excl-watermark {
          position: absolute; inset: 0; z-index: 3;
          display: flex; align-items: center; justify-content: center;
          pointer-events: none;
        }
        .gallery-excl-watermark span {
          background: rgba(255,77,79,0.75); color: #fff;
          padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 700;
          letter-spacing: 1px;
        }
        @media (max-width: 768px) {
          .gallery-grid {
            grid-template-columns: repeat(auto-fill, minmax(${minImageWidth * 2}px, 1fr));
          }
        }
      `}</style>

      {/* 頂部篩選欄 */}
      <div className="gallery-filter-bar">
        <span className="gallery-filter-bar-label">顯示篩選：</span>
        <Checkbox
          checked={hideSelected}
          onChange={e => setHideSelected(e.target.checked)}
        >
          不顯示已選用圖片
        </Checkbox>
        <Checkbox
          checked={hideExcluded}
          onChange={e => setHideExcluded(e.target.checked)}
        >
          不顯示未選取的圖片
        </Checkbox>
      </div>

      <Image.PreviewGroup preview={{ items: allImageUrls }}>
        {groupedMedia.map((group, groupIndex) => {
          const { processVariables, eformValues = {}, subGroups = [] } = group;
          const hasPvGrouping = groupingPvs && groupingPvs.length > 0;
          const hasEformGrouping = groupingEformFields && groupingEformFields.length > 0;

          return (
            <div key={group.groupKey || groupIndex} className="gallery-group">
              {/* 分組標題：直接以分組欄位值作為大標題 */}
              <div className="gallery-group-title" style={{ marginBottom: '16px' }}>
                {(() => {
                  const parts = [];
                  if (hasPvGrouping && processVariables) {
                    Object.entries(processVariables)
                      .filter(([key, value]) => groupingPvs.includes(key) && value !== null && value !== undefined)
                      .forEach(([key, value]) => {
                        parts.push(`${getPvDisplayLabel(key, pvDefinitions)}: ${String(value)}`);
                      });
                  }
                  if (hasEformGrouping) {
                    groupingEformFields.forEach(({ eformName, fieldId, displayLabel }) => {
                      const val = eformValues[`${eformName}:${fieldId}`];
                      if (val !== null && val !== undefined) {
                        parts.push(`${displayLabel || fieldId}: ${String(val).trim().replace(/\s+/g, ' ')}`);
                      }
                    });
                  }
                  const titleText = parts.length > 0
                    ? parts.join('　｜　')
                    : (subGroups[0]?.workflowExecution?.workflowName || `群組 ${groupIndex + 1}`);
                  return (
                    <Title level={3} style={{ margin: 0, color: '#1e40af', letterSpacing: '0.5px' }}>
                      {titleText}
                    </Title>
                  );
                })()}
              </div>

              {/* 按狀態的子分組 */}
              {subGroups.map((subGroup, subGroupIndex) => {
                const { workflowExecution, mediaFiles, executions = [] } = subGroup;
                const subGroupKey = makeSubGroupKey(group.groupKey, subGroup.status);
                const selectedPaths = selectedByGroup[subGroupKey] || [];
                const selectedCount = selectedPaths.length;
                const totalCount = mediaFiles.length;

                // 根據篩選條件過濾要顯示的圖片
                const visibleFiles = mediaFiles.filter(file => {
                  const normalized = normalizeMediaFilePath(file.filePath);
                  const isFileSelected = selectedPaths.includes(normalized);
                  if (hideSelected && isFileSelected) return false;
                  if (hideExcluded && !isFileSelected) return false;
                  return true;
                });

                return (
                  <div
                    key={subGroupKey}
                    style={{ marginBottom: subGroupIndex < subGroups.length - 1 ? '24px' : '0' }}
                  >
                    {/* SubGroup 標題列 */}
                    <div style={{
                      marginBottom: '8px',
                      padding: '8px 12px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '8px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <Text type="secondary" style={{ fontSize: '13px', fontWeight: 500 }}>
                          {executions.length > 1
                            ? `IDs: ${executions.map(e => e.id).join(', ')} | `
                            : workflowExecution?.id
                              ? `ID: ${workflowExecution.id} | `
                              : ''}
                          狀態: {normalizeStatusForDisplay(subGroup.status)}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          共 {totalCount} {t('reports.images') || '張圖片'}
                          {(hideSelected || hideExcluded) && visibleFiles.length !== totalCount
                            ? `（顯示 ${visibleFiles.length}）`
                            : ''}
                        </Text>
                        {selectedCount > 0 ? (
                          <Tag color="green" style={{ margin: 0, fontSize: '12px' }}>
                            已選 {selectedCount}/{totalCount} 張
                          </Tag>
                        ) : (
                          <Tag color="default" style={{ margin: 0, fontSize: '12px', color: '#888' }}>
                            全部不列印（未選取）
                          </Tag>
                        )}
                      </div>

                      {onSelectionChange && (
                        <Space size="small">
                          <Button
                            size="small"
                            icon={<SelectOutlined />}
                            onClick={() => handleSelectAll(subGroupKey, mediaFiles)}
                            disabled={selectedCount === totalCount}
                          >
                            全選
                          </Button>
                          <Button
                            size="small"
                            danger
                            icon={<ClearOutlined />}
                            onClick={() => handleClearAll(subGroupKey)}
                            disabled={selectedCount === 0}
                          >
                            全清
                          </Button>
                        </Space>
                      )}
                    </div>

                    {visibleFiles.length === 0 ? (
                      <Empty
                        description={t('reports.noImagesInGroup') || '該分組沒有圖片'}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    ) : (
                      <div className="gallery-grid">
                        {visibleFiles.map((file) => {
                          const fileId = `${subGroupKey}_${normalizeMediaFilePath(file.filePath)}`;
                          const imageUrl = getImageUrl(file.filePath);
                          const loadState = imageLoadStates[fileId];
                          const orderNum = getFileOrder(subGroupKey, file.filePath);
                          const isSelected = orderNum !== null;

                          const itemClass = isSelected
                            ? 'gallery-item gallery-item-selected'
                            : 'gallery-item gallery-item-not-selected';

                          return (
                            <div key={fileId} className={itemClass}>

                              {/* 右上角：選取徽章（已選顯綠數字，未選顯空心圈 hover 才出現） */}
                              {isSelected ? (
                                <div
                                  className="gallery-sel-badge"
                                  title="取消選取"
                                  onClick={(e) => { e.stopPropagation(); handleToggleSelect(subGroupKey, file.filePath); }}
                                >
                                  {orderNum}
                                </div>
                              ) : (
                                <div
                                  className="gallery-sel-empty"
                                  title="選取此圖（要列印）"
                                  onClick={(e) => { e.stopPropagation(); handleToggleSelect(subGroupKey, file.filePath); }}
                                />
                              )}

                              {/* 不列印浮水印（所有未選取圖片均顯示） */}
                              {!isSelected && (
                                <div className="gallery-excl-watermark">
                                  <span style={{ background: 'rgba(120,120,120,0.55)' }}>
                                    不列印
                                  </span>
                                </div>
                              )}

                              {loadState === 'error' ? (
                                <div className="gallery-item-error">
                                  <FileImageOutlined style={{ fontSize: '24px', marginRight: '8px' }} />
                                  {t('reports.imageLoadFailed') || '圖片加載失敗'}
                                </div>
                              ) : (
                                <>
                                  {loadState !== 'loaded' && (
                                    <div
                                      className="gallery-item-loading"
                                      style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                      <Spin size="small" />
                                    </div>
                                  )}
                                  <Image
                                    src={imageUrl}
                                    alt={file.originalFileName || file.fileName}
                                    className="gallery-item-image"
                                    preview={{
                                      mask: (
                                        <div>
                                          <div>{file.originalFileName || file.fileName}</div>
                                          {file.fileSize && (
                                            <div style={{ fontSize: '12px', marginTop: '4px' }}>
                                              {formatFileSize(file.fileSize)}
                                            </div>
                                          )}
                                        </div>
                                      ),
                                    }}
                                    onLoad={() => handleImageLoad(fileId)}
                                    onError={() => handleImageError(fileId)}
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      objectPosition: 'center',
                                      opacity: loadState === 'loaded' ? 1 : 0,
                                      transition: 'opacity 0.2s ease',
                                    }}
                                  />
                                  <div className="gallery-item-overlay">
                                    <div>{file.originalFileName || file.fileName}</div>
                                    {file.fileSize && <div>{formatFileSize(file.fileSize)}</div>}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </Image.PreviewGroup>
    </div>
  );
};

export default MediaGallery;
